/**
 * NewsTagChip — playful colored tag (10 tokens).
 *
 * Source: F-FE-HOTNEWS §3 + design source §2.4 tag system locked.
 * Tag color tokens: HOTNEWS_TAG_COLOR_TOKENS in src/i18n/hotnews.ts.
 *
 * a11y: chip itself is a Link (or button when filterable). Color contrast
 * locked to >= 4.5:1 white text on saturated dark, dark text on saturated
 * light dark mode.
 */

import { Link } from 'react-router-dom';
import {
  HOTNEWS_TAG_COLOR_TOKENS,
  type PlayfulTag,
  resolveHotNews,
} from '../../i18n/hotnews';
import { useLang } from '../../use-lang';

export interface NewsTagChipProps {
  tag: PlayfulTag;
  /** When true, chip is a clickable Link to /news/tag/[tag]. Default true. */
  linkable?: boolean;
  /** Compact = smaller padding; default = full size. */
  size?: 'sm' | 'md';
}

export function NewsTagChip({ tag, linkable = true, size = 'md' }: NewsTagChipProps) {
  const { lang } = useLang();
  const copy = resolveHotNews(lang);
  const labelKey = `tag_${tag}` as keyof typeof copy;
  const label = (copy as Record<string, string>)[labelKey] ?? tag;
  const tokens = HOTNEWS_TAG_COLOR_TOKENS[tag];

  // S7 Group A WCAG AA fix (2026-05-05):
  //   - text uses `tokens.textLight` (darken(light, 30%)) for ≥4.5:1 contrast
  //     on tinted bg in light mode.
  //   - bg tint reduced from 14% to 10% to keep tint subtle while paired with
  //     darkened text. Combined ratio now ≥5.04:1 across all 10 chips.
  //   - dark mode flips text to `tokens.dark` via .news-tag-chip[data-tag]
  //     selector + filter override in global.css (already PASS at 4.76+).
  const textLight = tokens.textLight;
  const style: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: size === 'sm' ? '2px 8px' : '3px 10px',
    fontSize: size === 'sm' ? 11 : 12,
    fontWeight: 600,
    fontFamily: 'var(--font-sans)',
    borderRadius: 999,
    backgroundColor: `color-mix(in srgb, ${tokens.light} 10%, transparent)`,
    color: textLight,
    border: `1px solid color-mix(in srgb, ${tokens.light} 35%, transparent)`,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    letterSpacing: '0.005em',
  };

  const darkStyle: React.CSSProperties = {
    // dark mode override via CSS custom property fallback (see global.css)
  };

  if (linkable) {
    return (
      <Link
        to={`/news/tag/${tag}`}
        className="news-tag-chip"
        data-tag={tag}
        style={{ ...style, ...darkStyle }}
        aria-label={label}
      >
        {label}
      </Link>
    );
  }
  return (
    <span
      className="news-tag-chip"
      data-tag={tag}
      style={{ ...style, ...darkStyle }}
    >
      {label}
    </span>
  );
}
